// @ts-nocheck
/**
 * Excel template export for the agent flow.
 *
 * Hits the same /generate-dynamic-template/ backend endpoint that the
 * legacy ResultsDisplayMulti uses, so the produced .xlsx is identical
 * for the same mapping data. Kept intentionally narrow — the agent
 * mapping payload does not carry the comparative / cell-referencing
 * concerns that the legacy generator supports.
 */

const BASE = import.meta.env.VITE_BACKEND_BASE_URL;
const API_KEY = import.meta.env.VITE_API_KEY;

export interface FlatStreamRow {
  standardized_category: string;
  original_name: string;
  original_row_number: number;
  value: number;
  period_index: number;
  period_display_name: string;
}

/**
 * Convert the agent's FinalMapping into the flat per-(account, period)
 * shape that /generate-dynamic-template/ expects in `stream_data`.
 *
 * Also injects synthetic "Net Income" rows from ``period_breakdowns`` so
 * the template's mapped-NI check row (slot ``Net Income`` → row 131 in
 * the populator) is populated from the source sheet's benchmark values.
 * The agent intentionally excludes the source Net Income row from its
 * classified mapping (it's the reconciliation benchmark, not a line
 * item), which would otherwise leave that check row empty and make the
 * variance formula display the full NI instead of 0.
 *
 * ``selectedPeriodIndices`` (optional) restricts the export to the given
 * period_index set — both the account-value rows and the synthetic NI
 * benchmark rows are filtered against it. Omitting the argument keeps
 * the historic "ship everything" behaviour so older call sites and
 * tests stay green. The picker in FinalMappingCard always provides it
 * in the agent flow.
 */
export function flattenMappingForExport(
  mapping: {
    accounts: Array<{
      row_index: number;
      account_name: string | null;
      category: string | null;
      value_by_period: Record<string | number, number | null> | Array<number | null>;
    }>;
    periods: Array<{
      period_index: number;
      display_name: string;
    }>;
    period_breakdowns?: Array<{
      period_index: number;
      benchmark_value: number | null;
      benchmark_name?: string | null;
    }>;
    // Cumulative-YTD cube only: the benchmark Net Income de-accumulated to
    // monthly flows, positionally aligned to the FULL periods[] (same shape as
    // an account's value_by_period). Present iff the mapping was de-accumulated;
    // used for the synthetic NI rows so the hard-value export ties.
    net_income_benchmark?: {
      account_name?: string | null;
      value_by_period?: Array<number | null> | null;
    } | null;
    source_meta?: {
      header_end_row?: number | null;
      data_start_row?: number | null;
      sheet_name?: string | null;
      ni_row_index?: number | null;
    } | null;
  },
  selectedPeriodIndices?: number[],
): FlatStreamRow[] {
  const filter =
    selectedPeriodIndices && selectedPeriodIndices.length > 0
      ? new Set(selectedPeriodIndices)
      : null;
  const periodsForExport = (mapping.periods || []).filter(
    (p) => filter === null || filter.has(p.period_index),
  );
  // value_by_period may be a positional array aligned to the FULL periods[]
  // (new backend shape) or a legacy int/str-keyed dict. periodsForExport is a
  // filtered subset, so map period_index → its slot in the full periods[].
  const posByPidx = new Map<number, number>(
    (mapping.periods || []).map((p, i) => [p.period_index, i]),
  );
  const rows: FlatStreamRow[] = [];
  for (const acct of mapping.accounts || []) {
    if (!acct.category) continue;
    for (const period of periodsForExport) {
      const vbp = acct.value_by_period;
      let raw: number | null | undefined;
      if (Array.isArray(vbp)) {
        const pos = posByPidx.get(period.period_index);
        raw = pos == null ? null : vbp[pos];
      } else {
        // Legacy dict — keys may be string or number after JSON round-trip.
        raw =
          vbp?.[period.period_index] ??
          vbp?.[String(period.period_index)] ??
          null;
      }
      if (raw === null || raw === undefined) continue;
      rows.push({
        standardized_category: acct.category,
        original_name: acct.account_name || '',
        original_row_number: acct.row_index,
        value: Number(raw),
        period_index: period.period_index,
        period_display_name: period.display_name,
      });
    }
  }
  // Synthetic Net Income rows — one per period that has a benchmark.
  // When the mapping payload's ``source_meta`` carries the source NI
  // row (set by ``assemble_mapping`` from the agent's
  // ``verify_net_profit`` input), use it as ``original_row_number`` so
  // the populator's referencing branch can build a real
  // ``='Source Data'!Xn`` formula for the Net Income cell. Without it
  // the synthetic row keeps the ``-1`` sentinel and the populator
  // writes the direct value instead (less ideal but not blank).
  //
  // The populator keys ``line_item_rows`` by (category, original_name)
  // so reusing the same name across periods routes all values to the
  // single Net Income slot. The agent endpoint converts this absolute
  // row index to the offset semantics ``calculate_actual_source_row``
  // expects before invoking the populator.
  const niRowIndexRaw = mapping.source_meta?.ni_row_index;
  const niRowIndex =
    typeof niRowIndexRaw === 'number' && niRowIndexRaw >= 0
      ? niRowIndexRaw
      : -1;
  const periodLookup = new Map<number, string>();
  for (const p of periodsForExport) {
    periodLookup.set(p.period_index, p.display_name);
  }
  // Cumulative-YTD cube: the line-item rows above are de-accumulated to monthly
  // flows, so the NI benchmark must be de-accumulated in lockstep — otherwise the
  // hard-value (non-referencing) export writes monthly line items against a
  // cumulative NI and the template's check row can't tie. assemble_mapping
  // surfaces the de-accumulated monthly NI as net_income_benchmark.value_by_period
  // (positionally aligned to the full periods[], same as account value_by_period).
  // Absent on non-cube/legacy mappings -> fall back to the cumulative
  // period_breakdowns benchmark, which there already equals the monthly value.
  const niMonthly = mapping.net_income_benchmark?.value_by_period;
  if (Array.isArray(niMonthly) && niMonthly.length > 0) {
    const niName = mapping.net_income_benchmark?.account_name || 'Net Income';
    for (const period of periodsForExport) {
      const pos = posByPidx.get(period.period_index);
      const raw = pos == null ? null : niMonthly[pos];
      if (raw === null || raw === undefined) continue;
      rows.push({
        standardized_category: 'Net Income',
        original_name: niName,
        original_row_number: niRowIndex,
        value: Number(raw),
        period_index: period.period_index,
        period_display_name: period.display_name,
      });
    }
  } else {
    for (const pb of mapping.period_breakdowns || []) {
      if (pb?.benchmark_value == null) continue;
      if (filter !== null && !filter.has(pb.period_index)) continue;
      const displayName = periodLookup.get(pb.period_index);
      if (!displayName) continue;
      rows.push({
        standardized_category: 'Net Income',
        original_name: pb.benchmark_name || 'Net Income',
        original_row_number: niRowIndex,
        value: Number(pb.benchmark_value),
        period_index: pb.period_index,
        period_display_name: displayName,
      });
    }
  }
  return rows;
}

/**
 * POST the flattened stream_data to /generate-dynamic-template/, then
 * trigger a browser download from the returned blob. Resolves with the
 * downloaded filename for caller logging; rejects on non-2xx with the
 * backend `detail` field if present.
 */
export async function downloadMappingAsExcel(
  mapping: Parameters<typeof flattenMappingForExport>[0] & {
    file?: { filename?: string | null };
  },
  selectedPeriodIndices?: number[],
): Promise<string> {
  const streamData = flattenMappingForExport(mapping, selectedPeriodIndices);
  if (streamData.length === 0) {
    throw new Error('No exportable rows in this mapping.');
  }
  const inputName =
    mapping.file?.filename?.replace(/\.[^.]+$/, '') || 'Mapping';
  const body = {
    stream_data: streamData,
    template_structure: null,
    input_name: inputName,
  };

  const res = await fetch(`${BASE}/generate-dynamic-template/`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': API_KEY,
    },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    let detail = text;
    try {
      const parsed = JSON.parse(text);
      if (parsed?.detail) detail = parsed.detail;
    } catch {
      /* leave detail as raw text */
    }
    throw new Error(`Template generation failed (${res.status}): ${detail}`);
  }

  const blob = await res.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;

  const cd = res.headers.get('Content-Disposition') || '';
  const m = cd.match(/filename\*=UTF-8''([^;]+)|filename=([^;]+)/i);
  const serverName = m
    ? decodeURIComponent((m[1] || m[2] || '').replace(/"/g, '').trim())
    : null;
  const dateStr = new Date().toISOString().split('T')[0];
  const fallback = `${inputName} - mapping - ${dateStr}.xlsx`;
  const filename = serverName || fallback;
  a.download = filename;

  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
  return filename;
}

/**
 * Thrown when the agent export endpoint returns 404 because the source
 * file is no longer on disk — typed so FinalMappingCard can recognise it
 * and flip the cell-references toggle off for the retry.
 */
export class SourceFileMissingError extends Error {
  constructor(detail: string) {
    super(detail);
    this.name = 'SourceFileMissingError';
  }
}

/**
 * Agent-flow Excel download. Posts to /agent/sessions/{id}/export-mapping,
 * which reads the source file from disk server-side (no base64 round-trip)
 * and reuses the same template populator the legacy /generate-dynamic-template/
 * endpoint uses. ``enableReferencing=true`` produces the linked variant
 * (source sheet embedded as a second tab, populated cells become formulas
 * referencing it); false produces the formatted-only variant.
 *
 * The ``opts.include*`` flags mirror the export popover's per-tab checkboxes.
 * ``includeTrends`` / ``includeInsights`` each add a dedicated sheet built
 * from the session's stored analysis. ``includeEbitdaBridge`` adds a
 * dedicated Bridge sheet (every identified add-back) AND renders the
 * approved add-backs as a collapsed Adjusted EBITDA block below Reported
 * EBITDA on the P&L sheet.
 *
 * The returned ``sheets`` object reflects the X-Sheet-*-Included response
 * headers: false means the backend skipped that sheet (artifact missing or
 * invalidated). ``bridgeIncluded`` keeps its original meaning, scoped to the
 * in-P&L block only (false means no approved add-backs in the exported
 * periods). null anywhere means the header wasn't requested or isn't
 * readable.
 */
export async function downloadAgentMappingAsExcel(
  sessionId: string,
  mapping: Parameters<typeof flattenMappingForExport>[0] & {
    file?: { filename?: string | null };
  },
  selectedPeriodIndices: number[],
  enableReferencing: boolean,
  opts: {
    includeTrends?: boolean;
    includeInsights?: boolean;
    includeEbitdaBridge?: boolean;
  } = {},
): Promise<{
  filename: string;
  bridgeIncluded: boolean | null;
  sheets: {
    trends: boolean | null;
    insights: boolean | null;
    bridge: boolean | null;
  };
}> {
  const {
    includeTrends = false,
    includeInsights = false,
    includeEbitdaBridge = false,
  } = opts;
  const streamData = flattenMappingForExport(mapping, selectedPeriodIndices);
  if (streamData.length === 0) {
    throw new Error('No exportable rows in this mapping.');
  }
  const inputName =
    mapping.file?.filename?.replace(/\.[^.]+$/, '') || 'Mapping';

  const res = await fetch(
    `${BASE}/agent/sessions/${encodeURIComponent(sessionId)}/export-mapping`,
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': API_KEY,
      },
      body: JSON.stringify({
        mapping,
        stream_data: streamData,
        input_name: inputName,
        enable_referencing: enableReferencing,
        include_trends: includeTrends,
        include_insights: includeInsights,
        include_ebitda_bridge: includeEbitdaBridge,
      }),
    },
  );

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    let detail = text;
    try {
      const parsed = JSON.parse(text);
      if (parsed?.detail) detail = parsed.detail;
    } catch {
      /* leave detail as raw text */
    }
    if (res.status === 404) {
      // Surface as the typed error so callers can react (toggle off, retry).
      throw new SourceFileMissingError(detail || 'Source file unavailable');
    }
    throw new Error(`Template generation failed (${res.status}): ${detail}`);
  }

  const blob = await res.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;

  const cd = res.headers.get('Content-Disposition') || '';
  const m = cd.match(/filename\*=UTF-8''([^;]+)|filename=([^;]+)/i);
  const serverName = m
    ? decodeURIComponent((m[1] || m[2] || '').replace(/"/g, '').trim())
    : null;
  const dateStr = new Date().toISOString().split('T')[0];
  const suffix = enableReferencing ? 'linked' : 'formatted';
  const fallback = `${inputName} - ${suffix} - ${dateStr}.xlsx`;
  const filename = serverName || fallback;
  a.download = filename;

  const boolHeader = (name: string): boolean | null => {
    const value = res.headers.get(name);
    return value == null ? null : value === 'true';
  };
  const bridgeIncluded = boolHeader('X-EBITDA-Bridge-Included');
  const sheets = {
    trends: boolHeader('X-Sheet-Trends-Included'),
    insights: boolHeader('X-Sheet-Insights-Included'),
    bridge: boolHeader('X-Sheet-Bridge-Included'),
  };

  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
  return { filename, bridgeIncluded, sheets };
}

/**
 * Unified (Full Statements) export. Posts to
 * /agent/sessions/{parentId}/export-unified, which populates the ticked
 * statements server-side from the children's authoritative mappings — no
 * mapping/stream_data round-trip — and assembles ONE workbook with the
 * ticked analysis parts (Working Capital pack, Trends, Insights, EBITDA
 * Bridge).
 *
 * Referencing is strict on the server: a source file gone from /tmp
 * surfaces as SourceFileMissingError so the caller can flip the toggle off
 * and retry values-only (same contract as downloadAgentMappingAsExcel).
 * The returned ``sheets`` object reflects the X-Sheet-*-Included headers:
 * false means a ticked part was skipped (artifact missing or the periods
 * would not join); null means it wasn't requested.
 */
export async function downloadUnifiedWorkbook(
  sessionId: string,
  opts: {
    includePnl?: boolean;
    includeBs?: boolean;
    includeTrends?: boolean;
    includeWorkingCapital?: boolean;
    includeInsights?: boolean;
    includeEbitdaBridge?: boolean;
    enableReferencing?: boolean;
  } = {},
): Promise<{
  filename: string;
  bridgeIncluded: boolean | null;
  sheets: {
    ccc: boolean | null;
    trends: boolean | null;
    insights: boolean | null;
    bridge: boolean | null;
  };
}> {
  const {
    includePnl = true,
    includeBs = true,
    includeTrends = false,
    includeWorkingCapital = false,
    includeInsights = false,
    includeEbitdaBridge = false,
    enableReferencing = true,
  } = opts;

  const res = await fetch(
    `${BASE}/agent/sessions/${encodeURIComponent(sessionId)}/export-unified`,
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': API_KEY,
      },
      body: JSON.stringify({
        include_pnl: includePnl,
        include_bs: includeBs,
        include_trends: includeTrends,
        include_working_capital: includeWorkingCapital,
        include_insights: includeInsights,
        include_ebitda_bridge: includeEbitdaBridge,
        enable_referencing: enableReferencing,
      }),
    },
  );

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    let detail = text;
    try {
      const parsed = JSON.parse(text);
      if (parsed?.detail) detail = parsed.detail;
    } catch {
      /* leave detail as raw text */
    }
    if (res.status === 404) {
      throw new SourceFileMissingError(detail || 'Source file unavailable');
    }
    throw new Error(`Export failed (${res.status}): ${detail}`);
  }

  const blob = await res.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;

  const cd = res.headers.get('Content-Disposition') || '';
  const m = cd.match(/filename\*=UTF-8''([^;]+)|filename=([^;]+)/i);
  const serverName = m
    ? decodeURIComponent((m[1] || m[2] || '').replace(/"/g, '').trim())
    : null;
  const filename = serverName || 'full_statements_output.xlsx';
  a.download = filename;

  const boolHeader = (name: string): boolean | null => {
    const value = res.headers.get(name);
    return value == null ? null : value === 'true';
  };
  const sheets = {
    ccc: boolHeader('X-Sheet-CCC-Included'),
    trends: boolHeader('X-Sheet-Trends-Included'),
    insights: boolHeader('X-Sheet-Insights-Included'),
    bridge: boolHeader('X-Sheet-Bridge-Included'),
  };
  const bridgeIncluded = boolHeader('X-EBITDA-Bridge-Included');

  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
  return { filename, bridgeIncluded, sheets };
}

/**
 * Databook export. Posts (no body) to
 * /agent/sessions/{parentId}/export-databook, which fills the client's own
 * workbook from both children's mappings and returns it.
 *
 * Rejects with the server's ``detail`` text as the message: a 409 (the
 * statements are not both mapped yet, or a run is in flight) or a 422 whose
 * detail is written for the user to act on.
 *
 * The resolved object mirrors the X-Databook-* response headers; each is
 * null when the header is absent (or not readable cross-origin):
 *   periodWindow   the periods exported, e.g.
 *                  "FY23, FY24, FY25, TTM 10/25; months Mar 2022 to Oct 2025"
 *   months         number of months of data in the workbook
 *   droppedMonths  months that were left out
 *   unmapped       accounts that have no line in the client's template
 *   notes          short sentences on what the workbook leaves out or shows
 *                  as part-year (X-Databook-Notes, a JSON array of strings)
 */
export async function downloadDatabookWorkbook(
  sessionId: string,
  opts: { enableReferencing?: boolean } = {},
): Promise<{
  filename: string;
  periodWindow: string | null;
  months: number | null;
  droppedMonths: number | null;
  unmapped: number | null;
  notes: string[] | null;
}> {
  // Cell references default on, as on every other export.
  const enableReferencing = opts.enableReferencing ?? true;
  const res = await fetch(
    `${BASE}/agent/sessions/${encodeURIComponent(sessionId)}/export-databook`,
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'x-api-key': API_KEY, 'Content-Type': 'application/json' },
      body: JSON.stringify({ enable_referencing: enableReferencing }),
    },
  );

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    let detail = '';
    try {
      const d = JSON.parse(text)?.detail;
      detail =
        typeof d === 'string'
          ? d
          : d?.message || d?.error || (d ? JSON.stringify(d) : '');
    } catch {
      /* body not json */
    }
    // With references on, a 404 means the source workbooks are gone: typed
    // so the export button can turn references off for the retry.
    if (res.status === 404 && enableReferencing) {
      throw new SourceFileMissingError(detail);
    }
    throw new Error(detail || `Export failed (${res.status})`);
  }

  const blob = await res.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;

  const cd = res.headers.get('Content-Disposition') || '';
  const m = cd.match(/filename\*=UTF-8''([^;]+)|filename=([^;]+)/i);
  const serverName = m
    ? decodeURIComponent((m[1] || m[2] || '').replace(/"/g, '').trim())
    : null;
  const filename = serverName || 'databook_output.xlsx';
  a.download = filename;

  const countHeader = (name: string): number | null => {
    const value = res.headers.get(name);
    if (value == null || value.trim() === '') return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  };
  const notesHeader = (): string[] | null => {
    const value = res.headers.get('X-Databook-Notes');
    if (!value) return null;
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed)
        ? parsed.filter((n) => typeof n === 'string' && n.trim() !== '')
        : null;
    } catch {
      return null;
    }
  };
  const exported = {
    filename,
    periodWindow: res.headers.get('X-Databook-Window') || null,
    months: countHeader('X-Databook-Months'),
    droppedMonths: countHeader('X-Databook-Dropped-Months'),
    unmapped: countHeader('X-Databook-Unmapped'),
    notes: notesHeader(),
  };

  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
  return exported;
}
